import { Injectable } from '@angular/core';
import {
  App,
  AppKey,
  GetMultiAppRequest,
  ItemIdentifier,
  ItemsApiService,
  ListDependenciesRequest,
  ListDependenciesRequiredItem,
  ListDependenciesRequiredItems,
  PartnerApiService,
} from '@galaxy/marketplace-apps';
import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { InventoryItem, InventoryItemApp } from '../common/inventory-item';
import { areEditionIdsEqual, mapify } from '../item-selector/util';

export type InventoryDependencyItem = InventoryItem & {
  enabled?: boolean;
};

type MissingItems = { [key: string]: ListDependenciesRequiredItems };

@Injectable()
export class ItemDependencyService {
  constructor(
    private itemsApiService: ItemsApiService,
    private partnerApiService: PartnerApiService,
  ) {}

  listMissingRequirementsForItems(
    partnerId: string,
    marketId: string,
    targetItems: InventoryItem[],
    availableItems: InventoryItem[],
  ): Observable<Map<string, InventoryDependencyItem[]>> {
    // We are only concerned about app and add-on dependencies
    const filteredTargetItems = targetItems.filter((ti) => ti.type !== 'package');
    if (!filteredTargetItems.length) {
      return of(new Map());
    }

    // We only need to be concerned with available apps because only apps can satisfy dependencies
    const filteredAvailableItems = availableItems.filter((ai) => ai.type === 'app');
    return this.listMissingItems(partnerId, marketId, filteredTargetItems).pipe(
      switchMap((missingItems) => {
        const missingItemsMap = this.mapifyMissingItems(missingItems);
        const filteredMissingItemsMap = this.filterMissingItems(missingItemsMap, filteredAvailableItems);
        return this.convertMissingItemsToInventoryDependencyItems(partnerId, marketId, filteredMissingItemsMap);
      }),
    );
  }

  private listMissingItems(
    partnerId: string,
    marketId: string,
    targetItems: InventoryItem[],
  ): Observable<MissingItems> {
    const itemIdentifiers = targetItems.map((ti) => {
      const itemId = ti.itemId;
      let editionId = '';
      if (ti.type === 'app') {
        const app = ti as InventoryItemApp;
        editionId = app.edition?.editionId ?? '';
      }
      return new ItemIdentifier({ itemId: itemId, editionId: editionId });
    });
    return this.itemsApiService
      .listDependencies(
        new ListDependenciesRequest({
          sellerId: partnerId,
          storeId: marketId,
          items: itemIdentifiers,
        }),
      )
      .pipe(
        map((resp) => resp?.missingItems ?? {}),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );
  }

  private mapifyMissingItems(missingItems: MissingItems): Map<string, ListDependenciesRequiredItem[]> {
    return mapify(
      Object.entries(missingItems) || [],
      ([itemId]) => itemId,
      ([, requiredItems]) => requiredItems.requiredItems,
    );
  }

  private filterMissingItems(
    missingItemsMap: Map<string, ListDependenciesRequiredItem[]>,
    availableItems: InventoryItem[],
  ): Map<string, ListDependenciesRequiredItem[]> {
    const availableApps = availableItems as InventoryItemApp[];
    const filteredMissingItems = new Map<string, ListDependenciesRequiredItem[]>();
    missingItemsMap.forEach((rawRequiredItems, itemId) => {
      const filteredRequiredItems =
        rawRequiredItems?.filter((ri) => {
          const item = availableApps.find(
            (i) =>
              i.itemId === ri.itemId &&
              (!ri.editionRequirement?.editionRequired ||
                areEditionIdsEqual(i.edition?.editionId, ri.editionRequirement?.editionId)),
          );
          return !item;
        }) ?? [];
      if (filteredRequiredItems?.length) {
        filteredMissingItems.set(itemId, filteredRequiredItems);
      }
    });
    return filteredMissingItems;
  }

  private convertMissingItemsToInventoryDependencyItems(
    partnerId: string,
    marketId: string,
    missingItemsMap: Map<string, ListDependenciesRequiredItem[]>,
  ): Observable<Map<string, InventoryDependencyItem[]>> {
    const requiredItems = Array.from(missingItemsMap.values()).flat();
    const requiredAppKeys = requiredItems.map((ri) => new AppKey({ appId: ri.itemId }));
    const appsMap$ = this.getAppsMap(partnerId, marketId, requiredAppKeys);
    return appsMap$.pipe(
      map((appsMap) => {
        const inventoryItemsMap = new Map<string, InventoryDependencyItem[]>();
        missingItemsMap.forEach((requiredItems, itemId) => {
          const inventoryItems = requiredItems.map((ri) => {
            const app = appsMap.get(ri.itemId);
            return this.requiredItemToInventoryDependencyItem(ri, app!);
          });
          inventoryItemsMap.set(itemId, inventoryItems);
        });
        return inventoryItemsMap;
      }),
    );
  }

  private getAppsMap(partnerId: string, marketId: string, appKeys: AppKey[]): Observable<Map<string, App>> {
    if (!appKeys.length) {
      return of(new Map());
    }

    return this.partnerApiService
      .getMultiApp(
        new GetMultiAppRequest({
          partnerId: partnerId,
          marketId: marketId,
          appKeys: appKeys,
          projectionFilter: { paths: ['key', 'sharedMarketingInformation', 'editionInformation'] },
        }),
      )
      .pipe(
        map((resp) =>
          mapify(
            resp.apps ?? [],
            (app) => app.key?.appId,
            (app) => app,
          ),
        ),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );
  }

  private requiredItemToInventoryDependencyItem(
    requiredItem: ListDependenciesRequiredItem,
    app: App,
  ): InventoryDependencyItem {
    const editions = app?.editionInformation?.editions || [];
    let edition = null;
    if (editions.length > 0) {
      // If a specific edition is required, find it. Otherwise, default to the first edition.
      if (requiredItem.editionRequirement?.editionRequired) {
        edition = editions.find((e) =>
          areEditionIdsEqual(e.appKey?.editionId, requiredItem.editionRequirement?.editionId),
        );
      } else {
        edition = editions[0];
      }
    }
    return {
      itemId: requiredItem.itemId,
      edition: {
        editionId: edition?.appKey?.editionId ?? '',
        name: edition?.name ?? '',
      },
      name: app?.sharedMarketingInformation?.name ?? '',
      iconUrl: app?.sharedMarketingInformation?.iconUrl ?? '',
      type: 'app',
      quantity: 1,
      enabled: requiredItem.enabled ?? false,
    };
  }
}
